import { useState } from 'react';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

const s = {
  page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' },
  card: { width: 380, background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 14, padding: '36px 32px' },
  logo: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 32 },
  icon: { width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoText: { fontSize: 15, fontWeight: 500, letterSpacing: '0.1em', color: 'var(--gold)' },
  heading: { fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 6 },
  sub: { fontSize: 13, color: 'var(--text-secondary)', marginBottom: 28, lineHeight: 1.5 },
  field: { marginBottom: 14 },
  fieldLabel: { fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 6, display: 'block' },
  btn: { width: '100%', background: 'var(--gold)', color: 'var(--bg-base)', border: 'none', borderRadius: 8, padding: '11px', fontSize: 13, fontWeight: 500, cursor: 'pointer', marginTop: 8 },
  err: { fontSize: 12, color: 'var(--red)', marginTop: 10, background: 'var(--red-bg)', padding: '8px 12px', borderRadius: 6 },
  hint: { fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 20, lineHeight: 1.5 },
  back: { fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 14, display: 'block', textAlign: 'center', width: '100%' },
  qrBox: { background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 10, padding: 20, textAlign: 'center', marginBottom: 20 },
  steps: { marginBottom: 20 },
  step: { fontSize: 12, color: 'var(--text-secondary)', padding: '5px 0', display: 'flex', gap: 10, alignItems: 'flex-start', lineHeight: 1.5 },
  stepNum: { width: 18, height: 18, borderRadius: '50%', background: 'var(--gold-muted)', color: 'var(--gold)', fontSize: 10, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  codeInput: { textAlign: 'center', fontSize: 28, fontWeight: 500, letterSpacing: '0.3em', padding: '14px', color: 'var(--gold)' },
  secretBox: { background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 12px', fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)', wordBreak: 'break-all', marginBottom: 14, textAlign: 'center' },
};

const STEPS = { CLIENT_ID: 'client_id', SCAN_QR: 'scan_qr', ENTER_CODE: 'enter_code' };

export default function Login({ onSignup }) {
  const { login } = useAuth();
  const [step, setStep] = useState(STEPS.CLIENT_ID);
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');


  const handleClientId = async () => {
    if (!clientId.trim()) { setError('Please enter your client ID'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: clientId.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Access denied');
      setClientName(data.name || clientId);
      if (data.requires_2fa_setup) { await setupQR(clientId.trim(), data.name); }
      else { setStep(STEPS.ENTER_CODE); }
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  };

  const setupQR = async (cid, name) => {
    try {
      const res = await fetch(`${API}/login/setup-2fa`, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Shaheen-Key': process.env.REACT_APP_ADMIN_KEY || '' }, body: JSON.stringify({ client_id: cid, client_name: name || cid }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setQrUrl(data.qr_url); setSecret(data.secret); setStep(STEPS.SCAN_QR);
    } catch { setStep(STEPS.ENTER_CODE); }
  };

  const handleVerify = async () => {
    if (totpCode.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/login/verify`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ client_id: clientId.trim(), totp_code: totpCode }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      localStorage.setItem('shaheen_token', data.token);
      login(data.client);
    } catch (e) { setError(e.message); setTotpCode(''); } finally { setLoading(false); }
  };

  const handleCodeChange = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 6);
    setTotpCode(clean);
    setError('');
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.logo}>
          <div style={s.icon}><svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M12 2L4 5.5V11c0 4.97 3.45 9.61 8 11 4.55-1.39 8-6.03 8-11V5.5L12 2z" fill="var(--gold)" opacity="0.2" stroke="var(--gold)" strokeWidth="1.5" strokeLinejoin="round"/><path d="M8.5 12.5L11 15L15.5 9.5" stroke="var(--gold)" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg></div>
          <span style={s.logoText}>SHAHEEN</span>
        </div>

        {step === STEPS.CLIENT_ID && (<>
          <div style={s.heading}>Secure access</div>
          <div style={s.sub}>Enter your client ID to continue. You will verify your identity with Microsoft Authenticator.</div>
          <div style={s.field}><span style={s.fieldLabel}>CLIENT ID</span><input value={clientId} onChange={e => setClientId(e.target.value)} placeholder="Your client identifier" onKeyDown={e => e.key==='Enter' && handleClientId()} autoFocus /></div>
          {error && <div style={s.err}>{error}</div>}
          <button style={s.btn} onClick={handleClientId} disabled={loading}>{loading ? 'Checking...' : 'Continue'}</button>
          <div style={s.hint}>Access is by invitation only. <br/><span onClick={() => onSignup && onSignup()} style={{ color: 'var(--gold)', cursor: 'pointer', textDecoration: 'underline' }}>Apply for access →</span></div>
        </>)}

        {step === STEPS.SCAN_QR && (<>
          <div style={s.heading}>Set up Microsoft Authenticator</div>
          <div style={s.sub}>Scan this QR code to add Shaheen to your authenticator app.</div>
          <div style={s.qrBox}>{qrUrl ? <img src={qrUrl} alt="QR" style={{ width: 180, height: 180, border: '4px solid white', borderRadius: 8 }} /> : <div style={{ width: 180, height: 180, background: 'var(--border)', margin: '0 auto', borderRadius: 8 }} />}</div>
          <div style={s.steps}>{['Open Microsoft Authenticator','Tap + and select Work or school account','Choose Scan QR code','Scan the code above','Shaheen will appear in your app'].map((t,i) => <div key={i} style={s.step}><div style={s.stepNum}>{i+1}</div><span>{t}</span></div>)}</div>
          {secret && <><div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6 }}>Can't scan? Enter manually:</div><div style={s.secretBox}>{secret}</div></>}
          <button style={s.btn} onClick={() => { setStep(STEPS.ENTER_CODE); }}>I've added it — continue</button>
        </>)}

        {step === STEPS.ENTER_CODE && (<>
          <div style={s.heading}>Enter your code</div>
          <div style={s.sub}>Open Microsoft Authenticator and enter the 6-digit code for <strong style={{ color: 'var(--gold)' }}>Shaheen</strong>.</div>
          <div style={s.field}><span style={s.fieldLabel}>AUTHENTICATOR CODE</span><input style={s.codeInput} value={totpCode} onChange={e => handleCodeChange(e.target.value)} placeholder="000000" inputMode="numeric" autoFocus maxLength={6} /></div>
          {error && <div style={s.err}>{error}</div>}
          <button style={s.btn} onClick={handleVerify} disabled={loading || totpCode.length !== 6}>{loading ? 'Verifying...' : 'Sign in'}</button>
          <button style={s.back} onClick={() => { setStep(STEPS.CLIENT_ID); setError(''); setTotpCode(''); }}>Use a different client ID</button>
        </>)}
      </div>
    </div>
  );
}
